import { normalizeBackendUrl } from "@/lib/api";

export interface AuthUser {
  id: number;
  fullName: string;
  email: string;
  role: string;
  passwordChangedAt?: string | null;
  has2fa?: boolean;
  onboardingCompleted?: boolean;
  nickname?: string | null;
  leaderboardOptIn?: boolean;
}

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);
export const AUTH_ME_TIMEOUT_MS = 15_000;

export type FetchMeResult =
  | { ok: true; user: AuthUser }
  | { ok: false; unauthorized: boolean; message: string };

export async function fetchCurrentUser(authToken: string): Promise<FetchMeResult> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), AUTH_ME_TIMEOUT_MS);
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
      signal: controller.signal,
    });
    if (res.status === 401) {
      return { ok: false, unauthorized: true, message: "Your session expired. Please sign in again." };
    }
    if (!res.ok) {
      return { ok: false, unauthorized: false, message: "Could not load your profile. Please try again." };
    }
    const user = (await res.json()) as AuthUser;
    if (!user?.id) {
      return { ok: false, unauthorized: false, message: "Invalid profile response. Please try again." };
    }
    return { ok: true, user };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        ok: false,
        unauthorized: false,
        message: "Sign-in timed out. Check your connection and try again.",
      };
    }
    return {
      ok: false,
      unauthorized: false,
      message: "Network error while finishing sign-in. Please try again.",
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}
